import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import EssaysHubView from './EssaysHubView';
import { DeskButton, DeskModal, Field, inputStyle } from './essayShared';

const NEW_ESSAY_TYPES = [
  { value: 'COMMON_APP_PS', label: 'CommonApp Personal Statement', description: '650 word main essay', wordLimit: 650 },
  { value: 'COMMON_APP_AI', label: 'Additional Information', description: 'CommonApp optional section', wordLimit: 650 },
  { value: 'SUPPLEMENTAL', label: 'School Supplemental', description: 'School-specific essay', wordLimit: null },
  { value: 'UC_PIQ', label: 'UC Personal Insight', description: '350 words per prompt', wordLimit: 350 },
  { value: 'COALITION', label: 'Coalition Essay', description: '500–650 word main essay', wordLimit: 650 },
  { value: 'SCHOLARSHIP', label: 'Scholarship Essay', description: 'Scholarship-specific essay', wordLimit: null },
  { value: 'OTHER', label: 'Other Essay', description: 'Other application essay', wordLimit: null },
];

const SCHOOL_TYPES = ['SUPPLEMENTAL', 'UC_PIQ', 'SCHOLARSHIP', 'OTHER', 'COALITION'];

// The student's own essays hub.
export default function CollegeEssaysPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();

  const [essays, setEssays] = useState([]);
  const [shareCount, setShareCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([api.get('/essays'), api.get('/college-access').catch(() => null)])
      .then(([essaysRes, accessRes]) => {
        if (!alive) return;
        setEssays(essaysRes.data.essays || []);
        setShareCount((accessRes?.data.grants || []).filter((g) => g.status !== 'REVOKED').length);
      })
      .catch(() => {
        if (alive) toast.error('Failed to load your essays');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api]);

  const handleCreate = async (form) => {
    const payload = {
      ...form,
      wordLimit: form.wordLimit ? parseInt(form.wordLimit, 10) : null,
      collegeApplicationId: form.collegeApplicationId || null,
    };
    const res = await api.post('/essays', payload);
    setEssays((prev) => [res.data.essay, ...prev]);
    setCreating(false);
    toast.success('Essay created');
    navigate(`/college/essays/${res.data.essay.id}`);
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>
          Loading essays…
        </p>
      </div>
    );
  }

  const newEssayButton = (
    <DeskButton variant="dark" onClick={() => setCreating(true)}>
      + New essay
    </DeskButton>
  );

  return (
    <>
      <EssaysHubView
        essays={essays}
        viewer={{ kind: 'user', id: user?.id, name: user?.name, role: 'STUDENT' }}
        owner={{ kind: 'user', id: user?.id, name: user?.name, role: 'STUDENT' }}
        onOpen={(id) => navigate(`/college/essays/${id}`)}
        headerActions={
          <>
            <DeskButton onClick={() => navigate('/college/access')}>
              {shareCount > 0 ? `Shared with ${shareCount} →` : 'Share this desk →'}
            </DeskButton>
            {newEssayButton}
          </>
        }
        emptyAction={newEssayButton}
      />
      {creating && <NewEssayModal api={api} onClose={() => setCreating(false)} onCreate={handleCreate} />}
    </>
  );
}

// ── New essay ─────────────────────────────────────────────────────────────

function NewEssayModal({ api, onClose, onCreate }) {
  const [form, setForm] = useState({
    type: '',
    school: '',
    collegeApplicationId: '',
    title: '',
    wordLimit: '',
    prompt: '',
    googleDocsUrl: '',
  });
  const [applications, setApplications] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get('/college-applications')
      .then((res) => {
        if (alive) setApplications(res.data.applications || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [api]);

  const pickType = (type) => {
    const info = NEW_ESSAY_TYPES.find((t) => t.value === type);
    setForm((prev) => ({ ...prev, type, wordLimit: info?.wordLimit ? String(info.wordLimit) : prev.wordLimit }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.type) {
      toast.error('Pick an essay type first');
      return;
    }
    setSaving(true);
    try {
      await onCreate(form);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create essay');
    } finally {
      setSaving(false);
    }
  };

  const needsSchool = SCHOOL_TYPES.includes(form.type);

  return (
    <DeskModal eyebrow="One more to write" title="New essay" onClose={onClose} width={620}>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Field label="Essay type">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 }}>
            {NEW_ESSAY_TYPES.map((type) => {
              const active = form.type === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => pickType(type.value)}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: active ? '1px solid var(--college-ink)' : '1px solid var(--college-border)',
                    background: active ? 'var(--college-bg-alt)' : 'var(--college-panel)',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'var(--college-ink)' }}>{type.label}</p>
                  <p style={{ margin: '3px 0 0', fontSize: 11, color: 'var(--college-muted)' }}>{type.description}</p>
                </button>
              );
            })}
          </div>
        </Field>

        {needsSchool && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="School / organisation">
              <input
                style={inputStyle}
                value={form.school}
                onChange={(e) => setForm({ ...form, school: e.target.value })}
                placeholder="e.g. Yale, MIT, Gates Foundation"
              />
            </Field>
            <Field label="Link to your list" hint="Pulls in that school's deadline and plan">
              <select
                style={inputStyle}
                value={form.collegeApplicationId}
                onChange={(e) => setForm({ ...form, collegeApplicationId: e.target.value })}
              >
                <option value="">Not linked</option>
                {applications.map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.collegeName}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 14 }}>
          <Field label="Nickname">
            <input
              style={inputStyle}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. The Piano Teacher's Hands"
            />
          </Field>
          <Field label="Word limit">
            <input
              style={inputStyle}
              type="number"
              value={form.wordLimit}
              onChange={(e) => setForm({ ...form, wordLimit: e.target.value })}
              placeholder="650"
            />
          </Field>
        </div>

        <Field label="The prompt">
          <textarea
            style={{ ...inputStyle, minHeight: 84, resize: 'vertical', lineHeight: 1.5 }}
            value={form.prompt}
            onChange={(e) => setForm({ ...form, prompt: e.target.value })}
            placeholder="Paste the prompt here…"
          />
        </Field>

        <Field label="Google Doc" hint="Optional — you can attach drafts later">
          <input
            style={inputStyle}
            type="url"
            value={form.googleDocsUrl}
            onChange={(e) => setForm({ ...form, googleDocsUrl: e.target.value })}
            placeholder="https://docs.google.com/…"
          />
        </Field>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            paddingTop: 16,
            borderTop: '1px solid var(--college-border)',
          }}
        >
          <DeskButton onClick={onClose}>Cancel</DeskButton>
          <DeskButton variant="dark" type="submit" disabled={saving}>
            {saving ? 'Creating…' : 'Create essay'}
          </DeskButton>
        </div>
      </form>
    </DeskModal>
  );
}
